import videojs from 'video.js';

import BannerLink from './banner/banner-link.js';
import { getDominantColor } from './banner/get-dominant-color.js';
import { generateEndedOverlay } from './banner/ended-modal-template.js';

import './banner.css';
import ModalDialog from '../../components/modal-dialog.js';
import Overlay, { OverlayPosition } from '../../components/overlay.js';

const _bannerLink = new WeakMap();
const _img = new WeakMap();
const _bannerWidth = new WeakMap();
const _bannerHeight = new WeakMap();
const _enabled = new WeakMap();
const _isLooping = new WeakMap();

const _endedEnabled = new WeakMap();
const _renderIn360 = new WeakMap();
const _endedModal = new WeakMap();
const _endedLink = new WeakMap();
const _endedImg = new WeakMap();
const _endedText = new WeakMap();
const _replayButton = new WeakMap();
const _returnButton = new WeakMap();
let localize;

/**
 * The Banner module.
 * @member {BannerPlugin} banner
 * @memberof MediasitePlayer#
 */

/**
 * Configures a banner image and link that may be displayed at the top of the player area.<br><br>
 * Note: the banner image will only be displayed if {@link BannerPlugin#enabled} is set to <code>true</code>, and {@link BannerPlugin#src} and {@link BannerPlugin#href} have each been set to a non-empty string.
 * @hideconstructor
 * @extends {external:"videojs.Plugin"}
 **/
class BannerPlugin extends videojs.getPlugin('plugin') {
    constructor(player, options) {
        localize = player.localize?.bind(player);
        super(player, options);
        _enabled.set(this, false);
        _endedEnabled.set(this, false);

        player.ready(() => {
            const bannerLink = new BannerLink(player);

            const img = document.createElement('img');

            img.setAttribute("alt", localize("Video banner"));
            bannerLink.el().appendChild(img);
            player.layout().addChild(bannerLink);

            _bannerLink.set(this, bannerLink);
            _img.set(this, img);

            this.enabled = false;

            const overlayContents = generateEndedOverlay(player);
            const endedLink = overlayContents.querySelector('a');
            _endedLink.set(this, endedLink);

            const endedImg = overlayContents.querySelector('img');
            _endedImg.set(this, endedImg);

            const endedText = overlayContents.querySelector('.vjs-presentation-ended-text');
            _endedText.set(this, endedText);

            const replayButton = overlayContents.querySelector('.mediasite-player__presentation-ended-replay-button');
            this.on(replayButton, 'click', this.handleClickedReplay);
            _replayButton.set(this, replayButton);

            const returnButton = overlayContents.querySelector('.mediasite-player__presentation-ended-return-button');
            this.on(returnButton, 'click', this.handleClickedReturn);
            _returnButton.set(this, returnButton);

            const endedOverlay = new Overlay(player, {
                text: '',
                position: OverlayPosition.Center
            });

            for (const childEl of Array.from(overlayContents.children)) {
                endedOverlay.el().appendChild(childEl);
            }

            const endedModal = new ModalDialog(player, {
                content: [endedOverlay.el()],
                solidBackground: false,
                uncloseable: true,
                temporary: false,
                label: localize('Presentation Ended'),
                showPlayerControls: !this.#shouldHideReplay()
            });
            endedModal.addClass('mediasite-player__presentation-ended-modal');
            player.layout().addChild(endedModal);
            _endedModal.set(this, endedModal);

            this.on(player, 'ended', this.#handlePresentationEnded);
            
        });
    }

    triggerResize() {
        this.player.trigger('playerresize');
    }

    updateLayout() {
        const bannerLink = _bannerLink.get(this);
        if (this.src && this.enabled) {
            bannerLink.removeClass('vjs-hidden');
        }
        else {
            bannerLink.addClass('vjs-hidden');
        }
        this.triggerResize();
    }

    /**
     * The path to an image to display.
     * @type {string}
     * @example
     * player.banner.src = '/path/to/image.jpg';
     */
    get src() {
        return _img.get(this)?.src;
    }

    set src(src) {
        this.player.ready(() => {
            _img.get(this).src = src;
            this.updateLayout();
        });
    }

    /**
     * A link to which the banner should point.
     * @type {string}
     * @example
     * player.banner.href = '/path/to/web/page/';
     */
    get href() {
        return _bannerLink.get(this).el().href;
    }

    set href(href) {
        this.player.ready(() => {
            const bannerEl = _bannerLink.get(this).el();
            if (!href?.length) {
                bannerEl.removeAttribute('href');
                bannerEl.setAttribute('tabindex', -1);
                bannerEl.setAttribute('aria-disabled', true);
            } else {
                bannerEl.href = href;
            }
            this.updateLayout();
        });
    }

    /**
     * A boolean indicating whether or not the Banner feature is enabled.
     * @type {boolean}
     * @example
     * player.banner.enabled = true;
     */
    get enabled() {
        return _enabled.get(this);
    }

    set enabled(enabled) {
        this.player.ready(() => {
            _enabled.set(this, enabled);
            this.updateLayout();
        });
    }

    /**
     * Configures how the banner is positioned in the layout.
     * @type {'Static'|'AutoHide'}
     * @example
     * player.banner.behavior = 'AutoHide';
     */
    get behavior() {
        return _bannerLink.get(this)?.behavior;
    }

    set behavior(behavior) {
        this.player.ready(() => {
            _bannerLink.get(this).behavior = behavior;
            this.updateLayout();
        });
        
    }

    /**
     * The desired display width of the banner.
     * @type {number}
     * @example
     * player.banner.width = 500;
     */
    get width() {
        return parseInt(_bannerWidth.get(this), 10);
    }

    set width(width) {
        this.player.ready(() => {
            _bannerLink.get(this).el().style.setProperty('--banner-width', width + "px");
            _bannerWidth.set(this, width);
            this.triggerResize();
        });
    }

    /**
     * The desired display height of the banner.
     * @type {number}
     * @example
     * player.banner.height = 100;
     */
    get height() {
        return parseInt(_bannerHeight.get(this), 10);
    }

    set height(height) {
        this.player.ready(() => {
            _bannerLink.get(this).el().style.setProperty('--banner-height', height + "px");
            _bannerHeight.set(this, height);
            this.triggerResize();
        });
    }

    /** 
     * The path to a banner image to display when the presentation has ended.
     * @type {string}
     * @example
     * player.banner.endedSrc = '/path/to/image.jpg';
     */
    get endedSrc() {
        return _endedImg.get(this)?.src;
    }

    set endedSrc(endedSrc) {
        this.player.ready(() => {
            const endedImg = _endedImg.get(this);
            const endedText = _endedText.get(this);
            if (endedSrc) {
                endedImg.src = endedSrc;
                endedImg.style.display = "block";
                endedText.style.display = "none";

                getDominantColor(endedSrc).then(color => {
                    const endedModal = _endedModal.get(this);
                    endedModal.el().style.setProperty('--ended-banner-bg-color', `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`);
                });
            }
            else {
                endedImg.removeAttribute('src');
                endedImg.style.display = "none";
                endedText.style.display = "block";
            }
        });
    }

    /**
     * A URL to use as a link for the Presentation Ended banner image, if present.
     * @type {string}
     * @example
     * player.banner.endedHref = '/path/to/web/page';
     */
    get endedHref() {
        return _endedLink.get(this)?.href;
    }

    set endedHref(endedHref) {
        this.player.ready(() => {
            const endedLink = _endedLink.get(this);
            if (endedHref) {
                endedLink.href = endedHref;
            }
            else {
                endedLink.removeAttribute('href');
            }
        });
    }

    /**
     * The desired display width of the Presentation Ended banner.
     * @type {number}
     * @example
     * player.banner.endedWidth = 500;
     */
    get endedWidth() {
        const endedImg = _endedImg.get(this);
        return endedImg ? parseInt(endedImg.width, 10) : 0;
    }

    set endedWidth(endedWidth) {
        this.player.ready(() => {
            const endedImg = _endedImg.get(this);
            endedImg.width = endedWidth;
            _endedModal.get(this).el().style.setProperty('--ended-banner-width', `${endedWidth}px`);
        });
    }

    /**
     * The desired display height of the Presentation Ended banner.
     * @type {number}
     * @example
     * player.banner.endedHeight = 100;
     */
    get endedHeight() {
        const endedImg = _endedImg.get(this);
        return endedImg ? parseInt(endedImg.height, 10) : 0;
    }

    set endedHeight(endedHeight) {
        this.player.ready(() => {
            const endedImg = _endedImg.get(this);
            endedImg.height = endedHeight;
        });
    }

    /**
     * Whether or not to show the "Presentation Ended" banner at the end of the presentation.
     * @type {boolean}
     * @example
     * player.banner.endedEnabled = true;
     */
    get endedEnabled() {
        return _endedEnabled.get(this);
    }

    set endedEnabled(endedEnabled) {
        _endedEnabled.set(this, endedEnabled);

        if (endedEnabled && this.player.currentTime() === this.player.duration()) {
            this.#handlePresentationEnded();
        }
        else {
            const endedModal = _endedModal.get(this);
            if (endedModal?.opened()) {
                endedModal.close();
            }
        }
    }

    /**
    * A boolean indicating whether or not we should render in XR or not
    * @type {boolean}
    * @example
    * player.banner.renderIn360 = true;
    */
    get renderIn360() {
        return _renderIn360.get(this);
    }

    set renderIn360(renderIn360) {
        _renderIn360.set(this, renderIn360);
    }

    /**
     * Whether or not we immediately restart the presentation when we hit the end of the presentation.
     * Done this way, rather than setting the element itself, to better work with our other plugins
     */ 
    get isLooping() {
        return _isLooping.get(this);
    }

    set isLooping(loop) {
        _isLooping.set(this, loop);
    }

    #handlePresentationEnded() {
        if (this.player.error()) {
            //if it is a live presentation and there is an error, the live plugin will determine whether it is actually
            //the end of the presentation or an error, so we shouldn't display a false presentation ended message
            return
        }
        //if we're looping, don't show the modal, but rather return back to the start of the presentation
        if (_isLooping.get(this)) {
            this.restartPresentation();
            return;
        }

        //if the playlist has another presentation, let it render its own modal, rather than the presentation ended modal
        const isUsingPlaylist = this.player.usingPlugin('playlist');
        if (isUsingPlaylist && this.player.playlist().isAutoPlaying()) {
            return;
        }

        const endedModal = _endedModal.get(this);
        //if (this.endedEnabled && this.endedSrc && !endedModal.opened?.()) {
        if (this.endedEnabled && !endedModal.opened?.()) {
            if (_renderIn360.get(this)) {
                let modalBtns = [];
                if (!this.#shouldHideReplay()) {
                    modalBtns.push({
                        text: localize("Replay"),
                        icon: "0xf116",
                        action: this.handleClickedReplay.bind(this)
                    });
                }

                if (!this.#shouldHideReturnToPrevious()) {
                    modalBtns.push({
                        text: localize("Back to Previous"),
                        icon: "0xf120",
                        action: this.handleClickedReturn.bind(this)
                    });
                }

                this.player.video360?.().displayModal("Presentation Ended", modalBtns);

                this.one(this.player, 'timeupdate', function () {
                    this.player.video360?.().removeModal();
                });
            } else {
                const replayButton = _replayButton.get(this);
                const hideReplay = this.#shouldHideReplay();
                this.#toggleDisplayBtn(replayButton, hideReplay);

                const returnButton = _returnButton.get(this);
                const hideReturn = this.#shouldHideReturnToPrevious();
                this.#toggleDisplayBtn(returnButton, hideReturn);

                endedModal.open();

                this.one(this.player, 'timeupdate', function () {
                    endedModal.close();
                });
            }
        }
    }

    #toggleDisplayBtn(button, isHidden) {
        if (isHidden) {
            button.classList.add('vjs-hidden');
        } else {
            button.classList.remove('vjs-hidden');
        }
    }

    #shouldHideReplay() {
        return this.player.usingPlugin('live') || this.player.usingPlugin('guided');
    }

    #shouldHideReturnToPrevious() {
        //if we're still in the orginial presentation, no need to have an option to return to it 
        const isOrginal = this.player.loader().isCurrentPresentationOriginal();

        if (isOrginal) {
            return true;
        }

        //if we're using the playlist plugin, ask it if the current presentation is in the playlist
        if (videojs.getPlugin("playlist")) {
            const currentPresentationId = this.player.loader().presentationId;
            const inPlaylist = this.player.playlist().isInPlaylist(currentPresentationId);

            return inPlaylist;
        }

        return false;
    }

    handleClickedReturn() {
        this.player.loader().loadPreviousPresentation();
    }

    handleClickedReplay() {
        if (_renderIn360.get(this)) {
            this.player.video360?.().removeModal();
        } else {
            _endedModal.get(this)?.close();
        }
        
        this.restartPresentation();
    }

    restartPresentation() {
        this.player.currentTime(0);
        this.player.one('timeupdate', () => {
            this.player.play();
        });
    }

    dispose() {
        const bannerLink = _bannerLink.get(this);
        const endedModal = _endedModal.get(this);
        this.player.layout().removeChild(bannerLink);
        this.player.layout().removeChild(endedModal);
    }
}

export default BannerPlugin;
